import { useFeatureFlags } from "@/lib/backend/utils/hooks/use-feature-flags";

/**
 * Which half of the app an organization gets.
 *
 * The herziening (app#108) replaced screens rather than adding them: Vandaag, Diensten, het
 * dienstscherm, Afgerond en Ontdek. Both halves now live in one bundle, and these flags decide per
 * organization which one renders. That is what makes the way back instant — the alternative was an
 * app release through two stores.
 *
 * Three rules hold this together:
 *
 * 1. **Off is the current app.** Every flag defaults to `false`, in the database and in the fallback
 *    the flags query returns when it fails. An organization that does nothing, or an app that
 *    cannot reach the api, renders exactly what it renders today.
 * 2. **The master switch is resolved here and nowhere else.** `appReworkEnabled` gates all six, so no
 *    screen has to remember to check two flags — and one field turns everything back off without
 *    anyone having to recall which six were on.
 * 3. **The seam is the page.** Components branch on these flags at the router, never halfway down a
 *    shared component, so each version stays internally consistent.
 *
 * Op de vlaggen van de organisatie ligt de keuze van de medewerker zelf, en die twee zijn al
 * samengevoegd voordat ze hier aankomen: `appReworkEnabled` is wat de SERVER voor deze medewerker
 * uitrekende uit de fase (`appReworkStage`) en zijn eigen keuze. Dat is met opzet -- de som staat
 * op één plek, en de app, het beheerscherm en de teller die zegt hoe ver de uitrol is lezen
 * allemaal dezelfde uitkomst. Twee uitwerkingen naast elkaar is precies hoe "hij staat aan" en "ik
 * zie hem niet" tegelijk waar worden.
 *
 * Die keuze stond eerder in de opslag van het toestel (`appReworkOptOut`). Daar was hij per
 * apparaat verschillend, onzichtbaar voor het beheer en niet te overrulen. Nu hangt hij aan het
 * lidmaatschap; zie `use-app-version` voor het schrijven en `use-app-rework-migration` voor wat er
 * met een oude lokale keuze gebeurt.
 *
 * While the query is still loading, everything reads `false`: the old app is the safe thing to
 * paint first, because it is what the employee already knows.
 */
/**
 * `discoverWindow` stond hier ook: Ontdek haalde dan de zichtbare maand plus twee op in plaats van
 * alles. Die is eruit, en niet uitgezet: hij besliste welk werk een medewerker kán zien, en dat is
 * geen knop. Ontdek haalt nu altijd alles op wat openstaat -- zie `openDiscoverWindow`.
 *
 * De api kent het veld `appReworkDiscoverWindowEnabled` nog wel. Die wordt hier niet meer gelezen.
 */
export type AppReworkFlags = {
  /** The Vandaag tab exists, and opening the app lands there. */
  today: boolean;
  /** Diensten as two tabs with agenda rows, and a shift as its own screen. */
  shifts: boolean;
  /** Afgerond as rows with status chips, and the completed-shift screen. */
  completed: boolean;
  /** Ontdek with a filter bar and scannable rows. */
  discover: boolean;
  /**
   * Het zetje op Vandaag: werk waarop je je kunt aanmelden, met de reden erbij.
   *
   * Stond ook op Ontdek, als blok bovenaan de lijst. Dat is eruit: het toonde dezelfde rijen die
   * eronder in de datumlijst al stonden, en op een scherm waar alles openstaat waarvoor je je kúnt
   * aanmelden zegt "voor jou" niets meer. Op Vandaag doet het wel iets -- daar is het het enige
   * werk dat je te zien krijgt.
   */
  forYou: boolean;
};

export const NO_APP_REWORK: AppReworkFlags = {
  today: false,
  shifts: false,
  completed: false,
  discover: false,
  forYou: false,
};

export function useAppReworkFlags(): AppReworkFlags {
  const { data } = useFeatureFlags();

  if (!data?.appReworkEnabled) {
    return NO_APP_REWORK;
  }

  return {
    today: data.appReworkTodayEnabled,
    shifts: data.appReworkShiftsEnabled,
    completed: data.appReworkCompletedEnabled,
    discover: data.appReworkDiscoverEnabled,
    forYou: data.appReworkForYouEnabled,
  };
}
